import { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { Box, Grid, Typography } from '@mui/material';
import IconButton from '@mui/material/IconButton';
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import CreateOutlinedIcon from '@mui/icons-material/CreateOutlined';
import { intervalToDuration } from 'date-fns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { TimeField } from '@mui/x-date-pickers/TimeField';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { Tooltip } from 'components/Tooltip';
import InfoIcon from '@mui/icons-material/Info';
//import * as moment from 'moment';
import { adminAPI } from 'services/admin';
import { Loaders } from 'components/Loader';
import { VideoCard } from 'components/VideoCard';
import { getImageURL } from 'utils/tableListFormat';
import { itemType, thumbAdornment } from 'constants/homeApp';
import { VideoOrSerieModal } from 'components/TableWithCheck/VideoOrSerieModal';
import {
  useTableSelectedList,
  tableSelectedListUtility,
} from 'hooks/useTableSelectedList';
import {
  INITIAL_RELATED_COLLECTION_LIST,
  RELATED_COLLECTION_LIST,
  RELATED_VIDEO_COLLECTION_LIST,
  VIDEO_COLLECTION_SUGGESTION,
} from 'constants/app';
import { useNotification } from 'hooks/useNotification';

function StepFour(props) {
  const { setNotify } = useNotification();
  const { setValue, videoResponse, viewFields } = props;

  const params = useParams();
  const [open, setOpen] = useState(false);
  const [isLoadingRelated, setIsLoadingRelated] = useState(false);
  const [suggestionTimeDisabled, setSuggestionTimeDisabled] = useState(true);
  const [suggestionTimeSeconds, setSuggestionTimeSeconds] = useState(null);

  const relatedVideoCollectionList = useTableSelectedList(
    RELATED_VIDEO_COLLECTION_LIST
  );
  const relatedCollectionList = useTableSelectedList(RELATED_COLLECTION_LIST);
  const initialRelatedCollectionList = useTableSelectedList(
    INITIAL_RELATED_COLLECTION_LIST
  );
  const videoOrCollectionSuggestion = useTableSelectedList(
    VIDEO_COLLECTION_SUGGESTION
  );

  const [initialRelatedContents] = useState(
    () => relatedVideoCollectionList.selectedItems
  );

  const nextContentSuggestion = useMemo(
    () => videoOrCollectionSuggestion.selectedItems?.[0],
    [videoOrCollectionSuggestion.selectedItems]
  );

  useEffect(() => {
    if (videoResponse?.nextSuggestionTimeSeconds) {
      let dateAndTime = intervalToDuration({
        start: 0,
        end: videoResponse?.nextSuggestionTimeSeconds * 1000,
      });
      setSuggestionTimeSeconds(
        new Date(
          2023,
          1,
          1,
          dateAndTime?.hours,
          dateAndTime?.minutes,
          dateAndTime?.seconds
        )
      );
    }
    setOpen(open);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const getRelatedContents = useCallback(async () => {
    try {
      if (initialRelatedContents?.length) return;

      setIsLoadingRelated(true);
      const relatedData = await adminAPI.get(
        `/admin/videos/${params?.id}/versions/${params?.version}}/related-contents`
      );

      relatedVideoCollectionList.setSelectedItems(
        tableSelectedListUtility.mapRemoteDataToTable(
          relatedData.relatedContents
        )
      );

      const collectionVideoResponse = relatedData?.collections
        .flat()
        .map(({ collectionId, ...rest }) => ({
          contentId: collectionId,
          id: collectionId,
          ...rest,
        }));
      relatedCollectionList.setSelectedItems(collectionVideoResponse);
      initialRelatedCollectionList.setSelectedItems(collectionVideoResponse);
    } catch (error) {
      setNotify({
        isOpen: true,
        message: error.message,
        type: 'error',
        title: 'Passo 4: Erro ao buscar vídeos e coleções associadas.',
      });
    } finally {
      setIsLoadingRelated(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialRelatedContents]);

  useEffect(() => {
    getRelatedContents();
  }, [getRelatedContents]);

  const handleSuggestionDisabled = () => {
    setSuggestionTimeDisabled(!suggestionTimeDisabled);
  };

  return (
    <div className="step-container" id="step-four">
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <div className="video_label-title">
            Coleções associadas{' '}
            <span style={{ fontSize: 12, color: '#6082B6' }}>(Máx. 5)</span>
          </div>
        </Grid>

        <div style={{ overflowY: 'scroll', width: '100%', maxHeight: '300px' }}>
          {isLoadingRelated && <Loaders.TableInfo />}
          {!isLoadingRelated &&
            relatedCollectionList.selectedItems?.map((collection, index) => (
              <VideoCard.Root key={collection?.collectionId}>
                <VideoCard.Image
                  variant="vertical"
                  image={getImageURL(collection, 'VERTICAL')}
                  size="small">
                  <VideoCard.CornerAdornment
                    name={thumbAdornment['COLLECTION']}
                  />
                </VideoCard.Image>
                <VideoCard.Description
                  title={itemType?.['COLLECTION']}
                  subtitle={collection?.title}
                  status={collection?.status}
                  targetAudiences={collection?.targetAudiences}
                  visibility={collection?.visibility}>
                  {/* <span style={{ marginTop: 10 }}>
                    <VideoCard.Chip
                      label={`Número de videos: ${collection?.videosCount || collection?.videos?.length || 0}`}
                    />
                  </span> */}
                </VideoCard.Description>
                <VideoCard.ActionsContainer>
                  <IconButton
                    disabled={viewFields}
                    aria-label="delete"
                    onClick={() => {
                      return alert('Funcionalidade em desenvolvimento');
                      // TODO remove alert and add delete
                      // relatedCollectionList.deleteSelectedItemsById(
                      //   collection.id
                      // );
                    }}
                    id="video-delete-colection-associated">
                    <DeleteOutlineOutlinedIcon
                      style={{ color: 'rgba(102, 112, 133, 1)' }}
                    />
                  </IconButton>
                </VideoCard.ActionsContainer>
              </VideoCard.Root>
            ))}
        </div>
        <Grid item xs={12}>
          <div className="step-button-group">
            <VideoOrSerieModal
              disabled={viewFields}
              itemName="Séries associadas"
              // TODO remove inDevelopment mode after api fix
              inDevelopment
              openButtonLabel="Adicionar Série"
              backButtonLabel="Voltar"
              listId={RELATED_COLLECTION_LIST}
              count={{
                selectedLength: relatedCollectionList.selectedItems?.length,
                maxLength: 5,
              }}
              limitExceeded={relatedCollectionList.selectedItems?.length >= 5}
              disableGutters
              fixedStep="series"
            />
          </div>
        </Grid>
      </Grid>
      {/* SUGESTÃO DE PRÓXIMO VÍDEO */}

      <Grid container spacing={2}>
        <Grid item xs={12}>
          <div
            className="video_label-title"
            style={{ marginTop: 40, marginBottom: 27 }}>
            Sugestão de próximo vídeo ou série
          </div>
          <span className="video-subtitle">
            {'Próximo vídeo/série sugerido'}{' '}
            <span style={{ fontSize: 12, color: '#6082B6' }}>(Máx. 1)</span>
          </span>
        </Grid>
        {nextContentSuggestion && (
          <>
            <div
              style={{
                width: '100%',
                maxHeight: '400px',
                display: 'flex',
                alignContent: 'space-between',
              }}>
              <VideoCard.Root>
                <VideoCard.Image
                  variant="square"
                  image={getImageURL(nextContentSuggestion, 'SQUARE')}
                  size="small">
                  <VideoCard.CornerAdornment
                    name={
                      thumbAdornment[
                        nextContentSuggestion?.contentType ||
                          nextContentSuggestion?.type ||
                          'VIDEO'
                      ]
                    }
                  />
                </VideoCard.Image>
                <Box>
                  <VideoCard.DescriptionDetail {...nextContentSuggestion} />
                  <Typography
                    variant="body2"
                    lineHeight="20px"
                    marginLeft="24px"
                    fontSize="12px"
                    color="#6F6B7D">
                    {(nextContentSuggestion.contentType ||
                      nextContentSuggestion?.type) === 'VIDEO' &&
                    nextContentSuggestion?.viewsLimit === 0
                      ? `Limite de Players:${nextContentSuggestion?.viewsLimit === 0}`
                      : ''}
                  </Typography>
                  <Typography
                    variant="body2"
                    lineHeight="20px"
                    fontSize="12px"
                    marginLeft="24px"
                    color="#6F6B7D">
                    {(nextContentSuggestion.contentType ||
                      nextContentSuggestion?.type) === 'VIDEO'
                      ? `Visualizações: ${nextContentSuggestion?.totalViews}`
                      : ''}
                  </Typography>
                </Box>
                <div style={{ width: 150, marginLeft: 60 }}>
                  <div style={{ display: 'flex' }}>
                    <p
                      style={{
                        fontWeight: 500,
                        fontSize: 12,
                        color: 'rgb(194, 194, 195)',
                      }}>
                      Sugerir tempo para exibição do vídeo(opcional)
                    </p>
                    <Tooltip.Info
                      title="Info"
                      description="Com base no video que está sendo criado/editado especifique o tempo antes do final do vídeo para mostrar a próxima sugestão no app, em caso da não especificação o tempo padrão para exibição é 60s antes do final do video."
                      show>
                      <InfoIcon sx={{ color: '#7CB9E8' }} />
                    </Tooltip.Info>
                  </div>
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                    }}>
                    <LocalizationProvider dateAdapter={AdapterDateFns}>
                      <TimeField
                        disabled={suggestionTimeDisabled}
                        style={{
                          fontWeight: 500,
                          color: 'rgba(111, 107, 125, 1)',
                        }}
                        id="video-nextsuggestion-time-set"
                        size="small"
                        variant="standard"
                        fullWidth
                        name={'suggestionTimeSeconds'}
                        value={suggestionTimeSeconds}
                        onChange={newValue => {
                          setSuggestionTimeSeconds(newValue);
                          setValue('suggestionTimeSeconds', newValue);
                          let d = new Date(newValue);
                          let seconds = d.getSeconds();
                          let minutes = d.getMinutes() * 60;
                          let hours = d.getHours() * 60 * 60;
                          if (d) {
                            setValue(
                              'nextSuggestionTimeSeconds',
                              seconds + hours + minutes
                            );
                          }
                        }}
                        format="HH:mm:ss"
                      />
                    </LocalizationProvider>
                    <CreateOutlinedIcon
                      onClick={handleSuggestionDisabled}
                      style={{
                        cursor: 'pointer',
                        color: 'rgba(102, 112, 133, 1)',
                      }}
                    />
                  </div>
                </div>
              </VideoCard.Root>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'right',
                  marginLeft: 60,
                }}>
                <VideoCard.ActionsContainer>
                  <IconButton
                    disabled={viewFields}
                    aria-label="delete"
                    onClick={() => {
                      videoOrCollectionSuggestion.deleteSelectedItemsById(
                        nextContentSuggestion.id
                      );
                    }}
                    id="video-remove-nextContentSugestion">
                    <DeleteOutlineOutlinedIcon
                      style={{ color: 'rgba(102, 112, 133, 1)' }}
                    />
                  </IconButton>
                </VideoCard.ActionsContainer>
              </div>
            </div>
          </>
        )}

        <Grid item xs={12}>
          <div className="step-button-group">
            <VideoOrSerieModal
              disabled={viewFields}
              itemName="Sugestão de vídeo ou série"
              openButtonLabel="Sugerir próximo vídeo ou série"
              backButtonLabel="Voltar"
              listId={VIDEO_COLLECTION_SUGGESTION}
              count={{
                selectedLength:
                  videoOrCollectionSuggestion.selectedItems?.length,
                maxLength: 1,
              }}
              limitExceeded={
                videoOrCollectionSuggestion.selectedItems?.length === 1
              }
              disableGutters
            />
          </div>
        </Grid>
      </Grid>
      {/* FIM SUGESTÃO DE PRÓXIMO VÍDEO */}
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <div
            className="video_label-title"
            style={{ marginTop: 40, marginBottom: 27 }}>
            Listagem de vídeos ou séries relacionadas
          </div>
          <span className="video-subtitle">
            {'Vídeos ou séries relacionadas'}{' '}
            <span style={{ fontSize: 12, color: '#6082B6' }}>(Máx. 20)</span>
          </span>
        </Grid>
        <div style={{ overflowY: 'scroll', width: '100%', maxHeight: '400px' }}>
          {isLoadingRelated && <Loaders.TableInfo />}
          {relatedVideoCollectionList.selectedItems?.map(related => (
            <VideoCard.Root key={related?.id}>
              <VideoCard.Image
                variant="vertical"
                image={getImageURL(related, 'VERTICAL')}
                size="small">
                <VideoCard.CornerAdornment
                  name={
                    thumbAdornment[related?.contentType || related?.type] ||
                    'VIDEO'
                  }
                />
              </VideoCard.Image>
              <Box>
                <VideoCard.DescriptionDetail {...related} />
                <Typography
                  variant="body2"
                  lineHeight="20px"
                  fontSize="12px"
                  marginLeft="24px"
                  color="#6F6B7D">
                  {(related?.contentType || related?.type) === 'VIDEO'
                    ? `Limite de Players: ${
                        related?.viewsLimit === 0
                          ? 'Ilimitado'
                          : related?.viewsLimit
                      } `
                    : ''}
                </Typography>
                <Typography
                  variant="body2"
                  lineHeight="20px"
                  fontSize="12px"
                  marginLeft="24px"
                  color="#6F6B7D">
                  {(related?.contentType || related?.type) === 'VIDEO'
                    ? `Visualizações: ${related?.totalViews}`
                    : ''}
                </Typography>
              </Box>
              <VideoCard.ActionsContainer>
                <IconButton
                  disabled={viewFields}
                  aria-label="delete"
                  onClick={() => {
                    relatedVideoCollectionList.deleteSelectedItemsById(
                      related.contentId || related.id
                    );
                  }}
                  id="video-delete-relatedContent">
                  <DeleteOutlineOutlinedIcon
                    style={{ color: 'rgba(102, 112, 133, 1)' }}
                  />
                </IconButton>
              </VideoCard.ActionsContainer>
            </VideoCard.Root>
          ))}
        </div>

        <Grid item xs={12} align="right">
          <VideoOrSerieModal
            disabled={viewFields}
            itemName="Listagem de vídeos ou séries relacionadas"
            openButtonLabel="Relacionar Vídeo ou Série"
            backButtonLabel="Voltar"
            listId={RELATED_VIDEO_COLLECTION_LIST}
            count={{
              selectedLength: relatedVideoCollectionList.selectedItems?.length,
              maxLength: 20,
            }}
            limitExceeded={
              relatedVideoCollectionList.selectedItems?.length >= 20
            }
            disableGutters
          />
        </Grid>
      </Grid>
    </div>
  );
}

export default StepFour;
